/**
 * VideoOverlayGenerator — Canvas-based video processing modal.
 *
 * Loads a template video, overlays the webcast title as centered text,
 * captures the composited output via MediaRecorder, and uploads it
 * to Azure Blob Storage.
 *
 * States: idle → loading → ready → generating → uploading → complete
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { X, Play, Download, Upload, Loader, CheckCircle } from "lucide-react";
import { getVideoUploadToken, saveIntroVideoUrl } from "../services/api";

const STATES = { IDLE: "idle", LOADING: "loading", READY: "ready", GENERATING: "generating", UPLOADING: "uploading", COMPLETE: "complete" };

function drawTitleOverlay(ctx, title, w, h) {
  const fontSize = title.length > 60 ? 36 : title.length > 40 ? 42 : 48;
  ctx.font = `700 ${fontSize}px 'DM Sans', 'Helvetica Neue', Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Semi-transparent dark bar
  const barHeight = Math.round(fontSize * 2.2);
  const barY = Math.round(h / 2 - barHeight / 2);
  ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
  ctx.fillRect(0, barY, w, barHeight);

  // White text with max width
  ctx.fillStyle = "#ffffff";
  ctx.fillText(title, w / 2, h / 2, w - 80);
}

export default function VideoOverlayGenerator({ templateUrl, title, eventId, onComplete, onClose }) {
  const [state, setState] = useState(STATES.IDLE);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const blobUrlRef = useRef(null);

  // Load template video as blob to avoid CORS canvas tainting
  useEffect(() => {
    if (!templateUrl) return;
    setState(STATES.LOADING);

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(templateUrl);
        if (!res.ok) throw new Error(`Failed to fetch template: ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        if (videoRef.current) {
          videoRef.current.src = url;
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [templateUrl]);

  const handleVideoLoaded = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw first frame as preview
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    drawTitleOverlay(ctx, title, canvas.width, canvas.height);

    setState(STATES.READY);
  }, [title]);

  const startGeneration = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    // Ensure DM Sans font is loaded
    try {
      await document.fonts.load("700 48px 'DM Sans'");
    } catch { /* best effort */ }

    setState(STATES.GENERATING);
    setProgress(0);
    setError(null);
    chunksRef.current = [];

    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream(30);

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: 5_000_000,
    });
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });

      // Create download URL
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);

      // Upload to blob storage
      setState(STATES.UPLOADING);
      try {
        const { sasUrl, blobName } = await getVideoUploadToken(eventId, `${title}.webm`, "video/webm");

        await fetch(sasUrl, {
          method: "PUT",
          headers: {
            "x-ms-blob-type": "BlockBlob",
            "Content-Type": "video/webm",
          },
          body: blob,
        });

        // Generate read URL (strip SAS query, use the base blob URL pattern)
        // The sasUrl from token endpoint is write-only; we need a read SAS.
        // Save via the intro-video endpoint which stores the URL.
        // For now, use the same URL base but the backend will regenerate a read SAS.
        // Actually, we'll re-fetch from the save endpoint.
        const result = await saveIntroVideoUrl(eventId, sasUrl.split("?")[0]);

        setState(STATES.COMPLETE);
        if (onComplete) onComplete({ videoUrl: result.introVideoUrl || sasUrl.split("?")[0] });
      } catch (err) {
        setError(`Upload failed: ${err.message}`);
        setState(STATES.COMPLETE); // still show download link
      }
    };

    recorder.start(1000); // capture in 1s chunks

    video.currentTime = 0;
    video.play();

    function draw() {
      if (video.ended || video.paused) {
        recorder.stop();
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        return;
      }
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      drawTitleOverlay(ctx, title, canvas.width, canvas.height);
      setProgress(video.duration ? (video.currentTime / video.duration) * 100 : 0);
      rafRef.current = requestAnimationFrame(draw);
    }
    rafRef.current = requestAnimationFrame(draw);

    // Stop recording when video ends
    video.onended = () => {
      // Draw final frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      drawTitleOverlay(ctx, title, canvas.width, canvas.height);
      setProgress(100);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      recorder.stop();
    };
  }, [title, eventId, onComplete]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
      if (downloadUrl) URL.revokeObjectURL(downloadUrl);
    };
  }, [downloadUrl]);

  const isGenerating = state === STATES.GENERATING;
  const isUploading = state === STATES.UPLOADING;
  const isComplete = state === STATES.COMPLETE;
  const isReady = state === STATES.READY;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center",
    }} onClick={(e) => { if (e.target === e.currentTarget && !isGenerating && !isUploading) onClose(); }}>
      <div style={{
        background: "#ffffff", borderRadius: 16, width: 720, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 80px rgba(0,0,0,0.3)",
      }}>
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 24px", borderBottom: "1px solid #e2e4ea",
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1d26" }}>Generate Intro Video</div>
            <div style={{ fontSize: 12, color: "#9ca3b4", marginTop: 2 }}>
              Overlay title on template video
            </div>
          </div>
          {!isGenerating && !isUploading && (
            <button onClick={onClose} style={{
              background: "none", border: "none", cursor: "pointer", color: "#9ca3b4", padding: 4,
            }}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: 24, overflow: "auto", flex: 1 }}>
          {/* Title preview */}
          <div style={{
            padding: "10px 16px", background: "#f8f9fb", borderRadius: 8,
            border: "1px solid #e2e4ea", marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, color: "#9ca3b4", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4 }}>
              Title Overlay
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1d26" }}>{title}</div>
          </div>

          {/* Canvas preview */}
          <div style={{
            position: "relative", borderRadius: 8, overflow: "hidden",
            background: "#000", marginBottom: 16,
          }}>
            <video
              ref={videoRef}
              style={{ display: "none" }}
              crossOrigin="anonymous"
              muted
              playsInline
              onLoadedData={handleVideoLoaded}
            />
            <canvas
              ref={canvasRef}
              style={{ width: "100%", height: "auto", display: "block" }}
            />

            {state === STATES.LOADING && (
              <div style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(0,0,0,0.7)", color: "#fff", fontSize: 14,
              }}>
                <Loader size={20} className="spin" style={{ marginRight: 8 }} />
                Loading template...
              </div>
            )}
            {state === STATES.IDLE && !error && (
              <div style={{
                width: "100%", height: 300, display: "flex", alignItems: "center", justifyContent: "center",
                color: "#9ca3b4", fontSize: 14,
              }}>
                Preparing...
              </div>
            )}
          </div>

          {/* Progress bar */}
          {(isGenerating || isUploading) && (
            <div style={{ marginBottom: 16 }}>
              <div style={{
                display: "flex", justifyContent: "space-between", fontSize: 12, color: "#5a6178", marginBottom: 6,
              }}>
                <span>{isUploading ? "Uploading..." : "Generating video..."}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div style={{
                height: 6, borderRadius: 3, background: "#e2e4ea", overflow: "hidden",
              }}>
                <div style={{
                  height: "100%", borderRadius: 3,
                  background: isUploading ? "#10b981" : "#3b82f6",
                  width: `${isUploading ? 100 : progress}%`,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              padding: "10px 16px", background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 8, color: "#dc2626", fontSize: 13, marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Complete state */}
          {isComplete && downloadUrl && (
            <div style={{
              padding: "12px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0",
              borderRadius: 8, display: "flex", alignItems: "center", gap: 12, marginBottom: 16,
            }}>
              <CheckCircle size={20} color="#16a34a" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#166534" }}>Video ready</div>
                <div style={{ fontSize: 12, color: "#15803d" }}>The intro video has been saved to this webcast.</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid #e2e4ea",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          {isReady && (
            <button onClick={startGeneration} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "10px 24px", borderRadius: 8, border: "none",
              background: "#000f2d", color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}>
              <Play size={16} /> Generate Video
            </button>
          )}

          {isComplete && downloadUrl && (
            <>
              <a
                href={downloadUrl}
                download={`${title} - Intro.webm`}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "10px 24px", borderRadius: 8, border: "1px solid #e2e4ea",
                  background: "#fff", color: "#1a1d26", fontSize: 14, fontWeight: 600,
                  cursor: "pointer", textDecoration: "none", fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <Download size={16} /> Download
              </a>
              <button onClick={onClose} style={{
                padding: "10px 24px", borderRadius: 8, border: "none",
                background: "#000f2d", color: "#fff", fontSize: 14, fontWeight: 600,
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
              }}>
                Done
              </button>
            </>
          )}

          {(isGenerating || isUploading) && (
            <div style={{ fontSize: 13, color: "#9ca3b4", display: "flex", alignItems: "center", gap: 6 }}>
              <Loader size={14} className="spin" />
              {isUploading ? "Saving..." : "Processing... please wait"}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
